/** Key Results — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useGo, useNavigation } from "@refinedev/core";
import type { KeyResult } from "@aos/shared";

import { StatusBadge } from "@/components/app/StatusBadge";
import { RefLink } from "@/components/inspector";

import { ResourceListPage, RowActions } from "../_shared/ListScaffold";
import { keyResultProgress, ProgressBar } from "./progress";

/**
 * KeyResultList (D6) — key results on the shared list grammar. The title leads
 * (the row links to detail); the parent objective renders via RefLink (never a
 * raw id), then the black-only progress bar with its percentage, the current /
 * target values, and the status. Shared DataTable supplies the states.
 */
export function KeyResultList() {
  const go = useGo();
  const { editUrl, showUrl } = useNavigation();
  const [pendingDelete, setPendingDelete] = React.useState<KeyResult | null>(
    null,
  );

  const columns = useMemo<ColumnDef<KeyResult, unknown>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        id: "objective_id",
        accessorKey: "objective_id",
        header: "Objective",
        enableSorting: false,
        cell: ({ row }) => (
          <RefLink resource="objectives" id={row.original.objective_id} />
        ),
      },
      {
        id: "progress",
        header: "Progress",
        enableSorting: false,
        cell: ({ row }) => {
          const pct = keyResultProgress(row.original);
          return (
            <div className="flex w-44 items-center gap-2">
              <ProgressBar value={pct} />
              <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {pct}%
              </span>
            </div>
          );
        },
      },
      {
        id: "current",
        header: "Current / Target",
        enableSorting: false,
        cell: ({ row }) => {
          const unit = row.original.unit ? ` ${row.original.unit}` : "";
          return (
            <span className="tabular-nums text-muted-foreground">
              {row.original.current_value ?? 0}
              {unit} / {row.original.target_value ?? "—"}
              {row.original.target_value != null ? unit : ""}
            </span>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("key-results", row.original.id) })}
            onEdit={() => go({ to: editUrl("key-results", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  return (
    <ResourceListPage<KeyResult>
      resource="key-results"
      title="Key Results"
      description="The measurable signals that say whether each objective is on track."
      createLabel="New Key Result"
      columns={columns}
      searchPlaceholder="Search key results…"
      emptyMessage="No key results yet. Add one to start measuring progress."
      deleteEntityLabel="key result"
      deleteName={(kr) => kr.title}
      pendingDelete={pendingDelete}
      setPendingDelete={setPendingDelete}
    />
  );
}

export default KeyResultList;
